import { DEFAULT_QUERY_OPTIONS } from "#src/components/hooks/adapters/adapterUtils";
import { getResourceAdapterAndResource } from "#src/components/hooks/adapters/utils/getResourceAdapterAndResource";
import { useQueries } from "@tanstack/react-query";
import {
  LookupAnswerValueType,
  LookupWithSingleAttributeRefValueType,
  ResourceLookupType,
} from "@validereinc/domain";
import { useCallback, useMemo } from "react";

/**
 * Extracts IDs from a multi-lookup input value.
 * Handles various input formats: array of strings, array of objects with id, etc.
 */
export const extractMultiLookupIds = (
  inputValue?: LookupWithSingleAttributeRefValueType[]
): string[] | null => {
  return inputValue?.map((item) => item.id) ?? null;
};

/**
 * Collects all multi-lookup IDs from an array of items, grouped by entity type.
 * @param items Array of items to process
 * @param getMultiLookupConfig Function that returns the multi-lookup config for each item
 * @returns Map of entity type to set of IDs
 */
export const collectMultiLookupIdsByEntityType = <TItem>(
  items: TItem[],
  getMultiLookupConfig: (item: TItem) => Array<{
    inputValue: LookupWithSingleAttributeRefValueType[];
    entityType?: ResourceLookupType;
  }>
): Map<ResourceLookupType, Set<string>> => {
  const idsMap = new Map<ResourceLookupType, Set<string>>();

  items.forEach((item) => {
    const configs = getMultiLookupConfig(item);
    configs.forEach(({ inputValue, entityType }) => {
      if (!entityType) return;

      const ids = extractMultiLookupIds(inputValue);
      if (ids && ids.length > 0) {
        const existingSet = idsMap.get(entityType) ?? new Set<string>();
        ids.forEach((id) => existingSet.add(id));
        idsMap.set(entityType, existingSet);
      }
    });
  });

  return idsMap;
};

/**
 * Hook that fetches assets for all multi-lookup IDs grouped by entity type.
 * Returns a map of asset ID to asset details for quick lookup.
 */
export const useMultiLookupAssetDetails = (
  multiLookupIdsByEntityType: Map<ResourceLookupType, Set<string>>
): {
  assetDetailsMap: Map<
    string,
    { id: string; name: string; entity_type: ResourceLookupType }
  >;
  isLoading: boolean;
} => {
  const assetQueryConfigs = useMemo(() => {
    const configs: Array<{
      entityType: ResourceLookupType;
      queryKey: [string, string[]];
      queryFn: () => Promise<unknown>;
      enabled: boolean;
    }> = [];

    multiLookupIdsByEntityType.forEach((ids, entityType) => {
      const assetType = entityType;
      const { adapter, resource } = assetType
        ? getResourceAdapterAndResource(assetType)
        : { adapter: null, resource: null };

      if (adapter && resource && ids.size > 0) {
        configs.push({
          entityType,
          queryKey: [resource, Array.from(ids)],
          queryFn: () =>
            adapter.getList({
              filters: { id: Array.from(ids) },
            }),
          enabled: true,
        });
      }
    });

    return configs;
  }, [multiLookupIdsByEntityType]);

  const assetQueries = useQueries({
    queries: assetQueryConfigs.map((config) => ({
      queryKey: config.queryKey,
      queryFn: config.queryFn,
      enabled: config.enabled,
      ...DEFAULT_QUERY_OPTIONS,
    })),
  });

  const assetDetailsMap = useMemo(() => {
    const map = new Map<
      string,
      { id: string; name: string; entity_type: ResourceLookupType }
    >();

    assetQueries.forEach((query, index) => {
      const config = assetQueryConfigs[index];
      if (!config) return;

      const queryData = query.data as
        | { data: Array<{ id: string; name: string }> }
        | undefined;

      if (queryData?.data) {
        queryData.data.forEach((asset) => {
          map.set(asset.id, {
            id: asset.id,
            name: asset.name,
            entity_type: config.entityType,
          });
        });
      }
    });

    return map;
  }, [assetQueries, assetQueryConfigs]);

  const isLoading = assetQueries.some((query) => query.isLoading);

  return { assetDetailsMap, isLoading };
};

/**
 * Shapes a multi-lookup input value into the format expected by displayValueByDataType.
 * @param inputValue The input value (could be array of IDs, array of objects, etc.)
 * @param entityType The entity type for the lookup
 * @param assetDetailsMap Map of asset ID to asset details (from useMultiLookupAssetDetails)
 * @returns Array of LookupAnswerValueType or null if invalid
 */
export const shapeMultiLookupInputValue = (
  inputValue: LookupWithSingleAttributeRefValueType[],
  entityType: ResourceLookupType | undefined,
  assetDetailsMap: Map<
    string,
    { id: string; name: string; entity_type: ResourceLookupType }
  >
): LookupAnswerValueType[] | null => {
  if (!entityType) {
    return null;
  }

  const ids = extractMultiLookupIds(inputValue);
  if (!ids) {
    return null;
  }

  const shapedValues: LookupAnswerValueType[] = [];
  ids.forEach((id) => {
    const assetDetails = assetDetailsMap.get(id);
    if (assetDetails && assetDetails.entity_type === entityType) {
      shapedValues.push({
        id: assetDetails.id,
        name: assetDetails.name,
        entity_type: assetDetails.entity_type,
      });
    }
  });

  return shapedValues.length > 0 ? shapedValues : null;
};

/**
 * Hook that provides a shaped multi-lookup value function.
 * Use this when you have already fetched asset details and want to shape values.
 */
export const useShapeMultiLookupValue = (
  assetDetailsMap: Map<
    string,
    { id: string; name: string; entity_type: ResourceLookupType }
  >
) => {
  return useCallback(
    (
      inputValue: LookupWithSingleAttributeRefValueType[],
      entityType: ResourceLookupType
    ): LookupAnswerValueType[] | null => {
      return shapeMultiLookupInputValue(
        inputValue,
        entityType,
        assetDetailsMap
      );
    },
    [assetDetailsMap]
  );
};
